import { AppLink } from "@/components/ui/app-link";
import { Card, CardContent } from "@/components/ui/card";
import { DateTimePicker } from "@/components/ui/date-picker";
import { LocalRelative } from "@/components/ui/local-date";
import { Pagination } from "@/components/ui/pagination";
import { Select } from "@/components/ui/select";
import { api } from "@/lib/api";
import { isoToPragueWall, pragueWallToIso } from "@/lib/datetime";
import { paginationSearchFields } from "@/lib/search";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Clock, GitCommitVertical, MessageSquare, RefreshCw } from "lucide-react";
import { z } from "zod";

/**
 * `/activity` — global activity stream. Five sources (Hub journals,
 * comments, time entries, sync jobs, GitLab events) merge and
 * paginate server-side under `/api/activity`; the client just
 * renders the normalised `ActivityItem[]` it gets back and groups
 * them by relative date for display.
 *
 * Filter chips drive `?type=&userId=&projectId=&page=` on the
 * URL. The user dropdown is rendered as `disabled` to mirror the
 * Next page — it ships the user list for parity but wiring the
 * onChange to `useNavigate` is its own follow-up.
 */

const ACTIVITY_TYPES = ["journal", "comment", "time_entry", "sync_job"] as const;
type ActivityType = (typeof ACTIVITY_TYPES)[number];

const activitySearchSchema = z.object({
  type: z.enum(ACTIVITY_TYPES).optional(),
  userId: z.string().optional(),
  projectId: z.string().optional(),
  /** `datetime-local` strings (`YYYY-MM-DDTHH:mm`) bounding the feed. */
  from: z.string().optional(),
  to: z.string().optional(),
  ...paginationSearchFields,
});

type ActivitySearch = z.infer<typeof activitySearchSchema>;

type ActivityItem = {
  id: string;
  type: ActivityType;
  actorName: string | null;
  description: string;
  detail: string | null;
  timestamp: string;
  href: string | null;
};

type ActivityResponse = {
  items: ActivityItem[];
  page: number;
  totalPages: number;
  totalItems: number;
  users: Array<{ id: string; name: string | null }>;
  projects: Array<{ id: string; name: string }>;
};

type ActivityGroups = {
  Today: ActivityItem[];
  Yesterday: ActivityItem[];
  "This Week": ActivityItem[];
  Older: ActivityItem[];
};

function groupByDate(items: ActivityItem[]): ActivityGroups {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const weekAgo = new Date(today.getTime() - 6 * 86_400_000);

  const groups: ActivityGroups = {
    Today: [],
    Yesterday: [],
    "This Week": [],
    Older: [],
  };

  for (const item of items) {
    const d = new Date(item.timestamp);
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (dayStart >= today) groups.Today.push(item);
    else if (dayStart >= yesterday) groups.Yesterday.push(item);
    else if (dayStart >= weekAgo) groups["This Week"].push(item);
    else groups.Older.push(item);
  }

  return groups;
}

const typeIcon: Record<ActivityType, React.ComponentType<{ className?: string }>> = {
  journal: GitCommitVertical,
  comment: MessageSquare,
  time_entry: Clock,
  sync_job: RefreshCw,
};

const typeColor: Record<ActivityType, string> = {
  journal: "bg-blue-100 text-blue-700",
  comment: "bg-purple-100 text-purple-700",
  time_entry: "bg-cyan-100 text-cyan-700",
  sync_job: "bg-yellow-100 text-yellow-700",
};

const activityTypeLabels: Array<{ value: ActivityType; label: string }> = [
  { value: "journal", label: "Changes" },
  { value: "comment", label: "Comments" },
  { value: "time_entry", label: "Time Logged" },
  { value: "sync_job", label: "Syncs" },
];

export const Route = createFileRoute("/_app/activity")({
  validateSearch: activitySearchSchema,
  loaderDeps: ({ search }) => ({
    type: search.type,
    userId: search.userId,
    projectId: search.projectId,
    from: search.from,
    to: search.to,
    page: search.page,
  }),
  loader: ({ deps }) => {
    const params = new URLSearchParams();
    if (deps.type) params.set("type", deps.type);
    if (deps.userId) params.set("userId", deps.userId);
    if (deps.projectId) params.set("projectId", deps.projectId);
    if (deps.from) params.set("from", deps.from);
    if (deps.to) params.set("to", deps.to);
    if (deps.page) params.set("page", String(deps.page));
    const qs = params.toString();
    return api.get<ActivityResponse>(qs ? `/api/activity?${qs}` : "/api/activity");
  },
  component: ActivityPage,
});

/** Matches `PAGE_SIZE` in `server/routes/activity.ts`. */
const ACTIVITY_PAGE_SIZE = 30;

function ActivityPage() {
  const { items, page, totalItems, users, projects } = Route.useLoaderData();
  const search: ActivitySearch = Route.useSearch();
  const navigate = useNavigate();
  const grouped = groupByDate(items);

  function setFilter(key: "userId" | "projectId" | "from" | "to", value: string) {
    void navigate({
      to: "/activity",
      search: { ...search, [key]: value || undefined, page: undefined },
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold">Activity</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Global activity stream across all projects
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 flex-wrap">
          <TypeChip target={undefined} active={!search.type}>
            All
          </TypeChip>
          {activityTypeLabels.map((t) => (
            <TypeChip key={t.value} target={t.value} active={search.type === t.value}>
              {t.label}
            </TypeChip>
          ))}
        </div>

        {users.length > 0 && (
          <Select
            aria-label="Filter by user"
            size="sm"
            value={search.userId ?? ""}
            onChange={(v) => setFilter("userId", v)}
            options={[
              { value: "", label: "All users" },
              ...users.map((u) => ({ value: u.id, label: u.name ?? "" })),
            ]}
          />
        )}

        {projects.length > 0 && (
          <Select
            aria-label="Filter by project"
            size="sm"
            value={search.projectId ?? ""}
            onChange={(v) => setFilter("projectId", v)}
            options={[
              { value: "", label: "All projects" },
              ...projects.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
        )}

        <label
          htmlFor="activity-from"
          className="flex items-center gap-1 text-xs text-muted-foreground"
        >
          From
          <DateTimePicker
            id="activity-from"
            aria-label="Activity from"
            className="w-52"
            value={search.from ? isoToPragueWall(search.from) : ""}
            onChange={(wall) => setFilter("from", wall ? pragueWallToIso(wall) : "")}
          />
        </label>
        <label
          htmlFor="activity-to"
          className="flex items-center gap-1 text-xs text-muted-foreground"
        >
          To
          <DateTimePicker
            id="activity-to"
            aria-label="Activity to"
            className="w-52"
            value={search.to ? isoToPragueWall(search.to) : ""}
            onChange={(wall) => setFilter("to", wall ? pragueWallToIso(wall) : "")}
          />
        </label>
      </div>

      <div className="space-y-6">
        {Object.entries(grouped).map(([group, groupItems]) => {
          if (groupItems.length === 0) return null;
          return (
            <div key={group}>
              <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                {group}
              </h3>
              <Card>
                <CardContent className="p-0 divide-y">
                  {groupItems.map((item) => {
                    const Icon = typeIcon[item.type];
                    const colorClass = typeColor[item.type];
                    return (
                      <div
                        key={item.id}
                        className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
                      >
                        <div
                          className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${colorClass}`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">
                            <span className="font-medium">{item.actorName ?? "System"}</span>{" "}
                            <span className="text-muted-foreground">{item.description}</span>
                          </p>
                          {item.detail && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                              {item.detail}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-muted-foreground">
                            <LocalRelative value={item.timestamp} />
                          </span>
                          {item.href &&
                            (item.href.startsWith("http") ? (
                              <a
                                href={item.href}
                                className="text-xs text-primary hover:underline"
                                target="_blank"
                                rel="noreferrer"
                              >
                                View
                              </a>
                            ) : (
                              <AppLink
                                href={item.href}
                                className="text-xs text-primary hover:underline"
                              >
                                View
                              </AppLink>
                            ))}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-sm">No activity found.</p>
          </div>
        )}
      </div>

      <Pagination
        total={totalItems}
        page={page}
        pageSize={ACTIVITY_PAGE_SIZE}
        basePath="/activity"
        searchParams={{
          type: search.type,
          userId: search.userId,
          projectId: search.projectId,
          from: search.from,
          to: search.to,
        }}
      />
    </div>
  );
}

function TypeChip({
  target,
  active,
  children,
}: {
  target: ActivityType | undefined;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to="/activity"
      search={(prev) => ({ ...prev, type: target, page: undefined })}
      className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "border-border hover:bg-muted"
      }`}
    >
      {children}
    </Link>
  );
}
